import { useEffect } from 'react';

type EffectReturn = Promise<(() => void) | void>;
type EffectFunction = (isMounted: { current: boolean }) => EffectReturn;
type DependencyArray = unknown[];

/**
 * Works like useEffect, but accepts an async function as the effect
 * function and provides an isMounted object with a current attribute
 * that tells if the component where it's being used is still mounted.
 *
 * This is useful to avoid state updates after asynchronous operations
 * if the compponent has been unmounted, which would throw an error:
 *
 *   useAsyncEffect(async (isMounted) => {
 *     const result = await asyncOperation();
 *
 *     if (isMounted.current) {
 *       // It would fail if the component unmounted while
 *       // the asyncOperation was being carried out.
 *       setResult(result);
 *     }
 *   }, []);
 */
function useAsyncEffect(
  effectFunction: EffectFunction,
  dependencyArray: DependencyArray,
): void {
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const isMounted = { current: true };
    const result = effectFunction(isMounted);

    return () => {
      isMounted.current = false;

      (async () => {
        const cleanupFunction = await result;

        if (typeof cleanupFunction === 'function') {
          cleanupFunction();
        }
      })();
    };
  }, dependencyArray);
  /* eslint-enable react-hooks/exhaustive-deps */
}

export default useAsyncEffect;
